// Copyright (c) 2026 Celadon Development LLC, All rights reserved.
// Author Vasily Klyuev <vasily.klyuev@celadon.ae>
// Self-serve report export for bookshops.
//
// The shop picks a date range and downloads any of the three reports the
// office sees in COMS, scoped to itself. This replaced the nightly report
// email the client turned down: the figures are available whenever the shop
// wants them instead of arriving every evening whether wanted or not.
//
// No redux store behind this page. Everything it holds - two dates, which
// report is downloading, the last message - is over the moment the file is
// saved, and nothing else in the app reads any of it. A saga and four store
// files would add indirection without adding anything.
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';

import { Button, Input, Loader } from '../../../components';
import { getBookshopReportService } from '../store/service';

import styles from './styles';

// Keys match the Report parameter the service expects.
const REPORTS = [{
  key: 'vouchers',
  title: 'Bookshop vouchers',
  description: 'Every voucher issued in the range, with its balance, expiry date and status.',
}, {
  key: 'usage',
  title: 'Voucher usage',
  description: 'One row per redemption taken at the till - what was deducted, and what was left.',
}, {
  key: 'reconciliation',
  title: 'Reconciliation',
  description: 'Line-level detail of the books traded in: where each one has got to, and whether it has been paid for.',
}];

// The service accepts dd/MM/yyyy or yyyy-MM-dd. Sending ISO leaves no room
// for a day/month mix-up between this machine's locale and the server's.
const API_DATE = 'YYYY-MM-DD';

class BookshopReportsPage extends Component {
  static propTypes = {
    classes: PropTypes.object,
  };

  static defaultProps = {
    classes: {},
  };

  state = {
    // A month back is the range a shop asks for most often, and it means the
    // page opens with something valid to download rather than empty pickers.
    from: moment().subtract(1, 'month').startOf('day').toDate(),
    to: moment().startOf('day').toDate(),
    // Which report is being fetched, so only that row shows a spinner.
    downloading: null,
    message: null,
  };

  onDateChange = (value, name) => {
    if (!value) return;
    this.setState({ [name]: value, message: null });
  }

  // The range is checked here as well as on the server. The server has to
  // check it because it is reachable without this page; doing it here too
  // means an obvious mistake is answered immediately instead of after a
  // round trip.
  validate = () => {
    const { from, to } = this.state;

    if (!from || !to) return 'Pick both a start and an end date.';
    if (moment(to).isBefore(moment(from), 'day')) return 'The end date is before the start date.';
    if (moment(to).diff(moment(from), 'days') > 366) {
      return 'A report can cover at most a year at a time. Narrow the range and export again.';
    }

    return null;
  }

  /**
   * Turns the base64 CSV into a file on disk.
   *
   * A Blob and a temporary object URL rather than a data: URI - Safari caps
   * how long a data: URI download can be, and a reconciliation export over a
   * busy month is well past it.
   */
  save = (report) => {
    const binary = window.atob(report.ContentBase64);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: report.ContentType || 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = report.FileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Releases the blob. Without this the whole file stays in memory for as
    // long as the tab is open, and a shop exporting several reports in a row
    // would keep every one of them.
    window.URL.revokeObjectURL(url);
  }

  download = async (key) => {
    const invalid = this.validate();

    if (invalid) {
      this.setState({ message: { error: true, text: invalid } });
      return;
    }

    this.setState({ downloading: key, message: null });

    const response = await getBookshopReportService({
      CustomerId: localStorage.getItem('loggedID'),
      Token: localStorage.getItem('token'),
      Report: key,
      From: moment(this.state.from).format(API_DATE),
      To: moment(this.state.to).format(API_DATE),
    });

    if (!response || !response.ok) {
      this.setState({
        downloading: null,
        message: {
          error: true,
          text: (response && response.body && response.body.Reason)
            || 'The report could not be produced. Please try again.',
        },
      });
      return;
    }

    const report = response.body;

    // A range with nothing in it is a normal answer, not a failure - the
    // service still returns the column headings. Saying so beats handing the
    // shop a file with one line in it and letting them work it out.
    if (!report.RowCount) {
      this.setState({
        downloading: null,
        message: {
          error: false,
          text: `No activity in the ${report.Title} report between `
            + `${moment(this.state.from).format('DD/MM/YYYY')} and `
            + `${moment(this.state.to).format('DD/MM/YYYY')}.`,
        },
      });
      return;
    }

    this.save(report);

    this.setState({
      downloading: null,
      message: {
        error: false,
        text: `${report.Title} downloaded - ${report.RowCount} `
          + `${report.RowCount === 1 ? 'row' : 'rows'}.`,
      },
    });
  }

  renderReport = (report) => {
    const { classes } = this.props;
    const { downloading } = this.state;

    return (
      <div className={classes.reportPanel} key={report.key}>
        <p className={classes.reportTitle}>{report.title}</p>
        <p className={classes.reportDescription}>{report.description}</p>
        {downloading === report.key
          ? <Loader />
          : (
            <Button
              text={'download csv'}
              isMainButton
              style={classes.button}
              onPress={() => this.download(report.key)}
            />
          )}
      </div>
    );
  }

  render() {
    const { classes } = this.props;
    const { from, to, message } = this.state;

    return (
      <div>
        <h4 className={classes.screenTitle}>EXPORT REPORTS</h4>
        <div className={classes.container}>
          <p className={classes.intro}>
            {'Choose a date range and download your reports as a CSV, ready to open in Excel.'}
          </p>

          <div className={classes.dateRow}>
            <Input
              dateType
              name={'from'}
              label={'From'}
              value={from}
              labelStyle={classes.label}
              onChange={this.onDateChange}
            />
            <Input
              dateType
              name={'to'}
              label={'To'}
              value={to}
              labelStyle={classes.label}
              onChange={this.onDateChange}
            />
          </div>
          <p className={classes.help}>
            {'Date range is inclusive.'}
          </p>

          {message
            ? (
              <p className={message.error ? classes.error : classes.notice}>
                {message.text}
              </p>
            )
            : null}

          {REPORTS.map(this.renderReport)}
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(BookshopReportsPage);
